export type DataManagementFieldType = 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox' | 'editor' | 'json';

export type DataManagementFieldDataType = 'string' | 'number' | 'date' | 'json' | 'html' | 'boolean' | 'alphanumeric';

export interface DataManagementFieldConfig {
  field_name: string;
  label: string;
  field_type: DataManagementFieldType;
  field_data_type: DataManagementFieldDataType;
  is_required: boolean;
  placeholder?: string;
  disabled?: boolean;
  disabled_on_edit?: boolean;
  depends_on?: string;
  linked_id_field?: string;
  option_id_key?: string;
  rows?: number;
  step?: string;
  max_length?: number;
  min?: number;
  max?: number;
}

export const dataManagementFormConfig = {
  reviewConfigurations: [
    { field_name: 'reviewCycleName', label: 'Review Cycle Name', field_type: 'text', field_data_type: 'alphanumeric', is_required: true, max_length: 10 },
    { field_name: 'reviewCycleDescription', label: 'Description', field_type: 'textarea', field_data_type: 'alphanumeric', is_required: true, rows: 3 },
    { field_name: 'reviewYear', label: 'Review Year', field_type: 'number', field_data_type: 'number', is_required: true, min: 1, max: 9999 },
    { field_name: 'reviewYearType', label: 'Review Year Type', field_type: 'text', field_data_type: 'alphanumeric', is_required: true, max_length: 32 },
    { field_name: 'tieringStartDate', label: 'Tiering Start Date', field_type: 'date', field_data_type: 'date', is_required: false },
    { field_name: 'tieringEndDate', label: 'Tiering End Date', field_type: 'date', field_data_type: 'date', is_required: false },
    { field_name: 'contractStartDate', label: 'Contract Start Date', field_type: 'date', field_data_type: 'date', is_required: false },
    { field_name: 'contractEndDate', label: 'Contract End Date', field_type: 'date', field_data_type: 'date', is_required: false },
    { field_name: 'providerTypeId', label: 'Provider Type', field_type: 'select', field_data_type: 'number', is_required: true, placeholder: 'None' },
    { field_name: 'reviewCounter', label: 'Review Counter', field_type: 'number', field_data_type: 'number', is_required: false, min: 1, disabled: true },
    { field_name: 'isCurrent', label: 'Is Current', field_type: 'checkbox', field_data_type: 'boolean', is_required: false },
  ],
  tiers: [
    { field_name: 'tierName', label: 'Tier Name', field_type: 'text', field_data_type: 'string', is_required: true },
    { field_name: 'tierCode', label: 'Tier Code', field_type: 'text', field_data_type: 'string', is_required: true, disabled_on_edit: true },
    { field_name: 'tierIdentifier', label: 'Tier Identifier', field_type: 'text', field_data_type: 'string', is_required: true, disabled_on_edit: true },
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
    { field_name: 'additionalTierDetails', label: 'Additional Details', field_type: 'text', field_data_type: 'alphanumeric', is_required: false },
    { field_name: 'sequence', label: 'Sequence', field_type: 'number', field_data_type: 'number', is_required: true },
    { field_name: 'tierLevel', label: 'Tier Level', field_type: 'number', field_data_type: 'number', is_required: false },
    { field_name: 'isActualTier', label: 'Is Actual Tier', field_type: 'checkbox', field_data_type: 'boolean', is_required: false },
  ],
  measures: [
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
    { field_name: 'measureNumber', label: 'Measure Number', field_type: 'text', field_data_type: 'alphanumeric', is_required: true },
    { field_name: 'measureNumberFile', label: 'Measure Number File', field_type: 'text', field_data_type: 'alphanumeric', is_required: true },
  ],
  performanceAreas: [
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
    { field_name: 'performanceAreaTitle', label: 'Performance Area Title', field_type: 'text', field_data_type: 'alphanumeric', is_required: true },
    { field_name: 'performanceAreaDescription', label: 'Performance Area Description', field_type: 'textarea', field_data_type: 'alphanumeric', is_required: true, rows: 3 },
  ],
  standards: [
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
    { field_name: 'performanceAreaTitle', label: 'Performance Area', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Performance Area', linked_id_field: 'performanceAreaId', option_id_key: 'performanceAreaId', depends_on: 'providerType' },
    { field_name: 'title', label: 'Standard Title', field_type: 'text', field_data_type: 'alphanumeric', is_required: true },
    { field_name: 'description', label: 'Description', field_type: 'textarea', field_data_type: 'alphanumeric', is_required: true, rows: 3 },
  ],
  reviewStatus: [
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
    { field_name: 'statusName', label: 'Status Name', field_type: 'text', field_data_type: 'string', is_required: true },
    { field_name: 'statusCode', label: 'Status Code', field_type: 'text', field_data_type: 'string', is_required: true, disabled_on_edit: true },
    { field_name: 'sequence', label: 'Sequence', field_type: 'number', field_data_type: 'number', is_required: true },
  ],
  questions: [
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
    { field_name: 'reviewYear', label: 'Review Year', field_type: 'select', field_data_type: 'number', is_required: true, placeholder: 'Select Review Year', linked_id_field: 'reviewConfigId', option_id_key: 'reviewConfigId', depends_on: 'providerType' },
    { field_name: 'performanceAreaTitle', label: 'Performance Area', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Performance Area', linked_id_field: 'performanceAreaId', option_id_key: 'performanceAreaId', depends_on: 'providerType' },
    { field_name: 'standardTitle', label: 'Standard Title', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Standard', linked_id_field: 'standardId', option_id_key: 'standardId', depends_on: 'performanceAreaTitle' },
    { field_name: 'tierCode', label: 'Tier Code', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Tier Code', linked_id_field: 'tierId', option_id_key: 'tierId', depends_on: 'providerType' },
    { field_name: 'measureNumber', label: 'Measure Number', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Measure Number', linked_id_field: 'measureId', option_id_key: 'measureId', depends_on: 'providerType' },
    { field_name: 'questionText', label: 'Question Text', field_type: 'textarea', field_data_type: 'alphanumeric', is_required: true, rows: 3 },
    { field_name: 'questionHtmlText', label: 'Question HTML Text', field_type: 'editor', field_data_type: 'html', is_required: true },
    { field_name: 'dataFieldType', label: 'Data Field Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Data Field Type' },
    { field_name: 'sequence', label: 'Sequence', field_type: 'number', field_data_type: 'number', is_required: true, step: '0.01' },
    { field_name: 'conditionalDisplay', label: 'Conditional Display', field_type: 'json', field_data_type: 'json', is_required: false, placeholder: `{"canHide": "lower('guid')='n/no/resp'"}` },
  ],
  measureMapping: [
    { field_name: 'providerType', label: 'Provider Type', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Provider Type' },
     { field_name: 'reviewYear', label: 'Review Year', field_type: 'select', field_data_type: 'number', is_required: true, placeholder: 'Select Review Year', linked_id_field: 'reviewConfigId', option_id_key: 'reviewConfigId', depends_on: 'providerType' },
    { field_name: 'performanceAreaTitle', label: 'Performance Area Title', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Performance Area', linked_id_field: 'performanceAreaId', option_id_key: 'performanceAreaId', depends_on: 'providerType' },
    { field_name: 'tierName', label: 'Tier Name', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Tier', linked_id_field: 'tierId', option_id_key: 'tierId', depends_on: 'providerType' },
    { field_name: 'standardTitle', label: 'Standard Title', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Standard', linked_id_field: 'standardId', option_id_key: 'standardId', depends_on: 'providerType' },
    { field_name: 'measureNumber', label: 'Measure Number', field_type: 'select', field_data_type: 'alphanumeric', is_required: true, placeholder: 'Select Measure', linked_id_field: 'measureId', option_id_key: 'measureId', depends_on: 'providerType' },
    { field_name: 'measureTitle', label: 'Measure Title', field_type: 'text', field_data_type: 'alphanumeric', is_required: true },
    { field_name: 'measureDescription', label: 'Measure Description', field_type: 'textarea', field_data_type: 'alphanumeric', is_required: true, rows: 3 },
    { field_name: 'measureDescriptionHtml', label: 'Measure Description HTML', field_type: 'editor', field_data_type: 'html', is_required: true },
    { field_name: 'scoringCriteria', label: 'Scoring Criteria', field_type: 'editor', field_data_type: 'html', is_required: true },
    { field_name: 'measureStatusName', label: 'Measure Status', field_type: 'select', field_data_type: 'string', is_required: true, placeholder: 'Select Measure Status', linked_id_field: 'measureStatusId', option_id_key: 'measureStatusId' },
    { field_name: 'compositeGroupName', label: 'Composite Group', field_type: 'select', field_data_type: 'alphanumeric', is_required: false, placeholder: 'Select Composite Group', linked_id_field: 'compositeGroupId', option_id_key: 'compositeGroupId' },
  ],
} as const satisfies Record<string, readonly DataManagementFieldConfig[]>;

export type DataManagementFormConfig = typeof dataManagementFormConfig;

const isEmptyValue = (value: unknown) =>
  value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

// Letters, numbers, spaces and common punctuation; blocks markup/script-like input.
const TEXT_VALUE_PATTERN = /^[\p{L}\s.,'"()&/\\_:;#@%!?+-]*$/u;
const TEXT_ALPHANUMERIC_VALUE_PATTERN = /^[\p{L}\p{N}\s.,'"()&/\\_:;#@%!?+-]*$/u;

const isValidTextValue = (value: unknown) => typeof value !== 'string' || TEXT_VALUE_PATTERN.test(value);
const isValidAlphaNumericValue = (value: unknown) => typeof value !== 'string' || TEXT_ALPHANUMERIC_VALUE_PATTERN.test(value);

const isValidNumberValue = (value: unknown) =>
  value === '' || value === null || value === undefined || !Number.isNaN(Number(value));

const isValidDateValue = (value: unknown) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const isValidJsonValue = (value: unknown) => {
  if (typeof value !== 'string') return false;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
};

const HTML_VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const HTML_TAG_PATTERN = /<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g;

const isValidHtmlValue = (value: unknown) => {
  if (typeof value !== 'string') return false;
  const openTags: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = HTML_TAG_PATTERN.exec(value)) !== null) {
    const [fullTag, tagName] = match;
    const lowerTag = tagName.toLowerCase();
    if (HTML_VOID_TAGS.has(lowerTag) || fullTag.endsWith('/>')) continue;
    if (fullTag.startsWith('</')) {
      if (openTags.pop() !== lowerTag) return false;
    } else {
      openTags.push(lowerTag);
    }
  }
  return openTags.length === 0;
};

/** Returns a validation message for the field's configured data type, or null when valid. */
export const getFieldDataTypeError = (field: DataManagementFieldConfig, value: unknown): string | null => {
  if (isEmptyValue(value)) return null;
  switch (field.field_data_type) {
    case 'string':
      return isValidTextValue(value) ? null : `${field.label} contains invalid characters`;
    case 'number':
      return isValidNumberValue(value) ? null : `${field.label} must be a valid number`;
    case 'date':
      return isValidDateValue(value) ? null : `${field.label} must be a valid date`;
    case 'json':
      return isValidJsonValue(value) ? null : `${field.label} must be valid JSON`;
    case 'html':
      return isValidHtmlValue(value) ? null : `${field.label} must be valid HTML`;
    case 'alphanumeric':
      return isValidAlphaNumericValue(value)?null : `${field.label} must be valid value`
    default:
      return null;
  }
};

export const getMissingRequiredFields = (
  fields: readonly DataManagementFieldConfig[],
  values: Record<string, unknown> | null,
) => fields
  .filter(field => field.is_required)
  .filter(field => isEmptyValue(values?.[field.field_name]))
  .map(field => field.label);

export const getFormValidationErrors = (
  fields: readonly DataManagementFieldConfig[],
  values: Record<string, unknown> | null,
): string[] => {
  const errors: string[] = [];
  fields.forEach(field => {
    const value = values?.[field.field_name];
    if (field.is_required && isEmptyValue(value)) {
      errors.push(`${field.label} is required`);
      return;
    }
    const typeError = getFieldDataTypeError(field, value);
    if (typeError) errors.push(typeError);
  });
  return errors;
};

// Sends null instead of an empty value for optional fields so the API doesn't receive empty strings.
export const normalizeFieldValues = <T extends Record<string, unknown>>(
  fields: readonly DataManagementFieldConfig[],
  values: T | null,
): T => {
  const normalized: Record<string, unknown> = { ...(values || {}) };
  fields.forEach(field => {
    if (!field.is_required && isEmptyValue(normalized[field.field_name])) {
      normalized[field.field_name] = null;
    }
  });
  return normalized as T;
};